import { useQuery, useReactiveVar } from '@apollo/client';
import { Button } from '@mui/material';
import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { clearInMemoryVar, setInMemoryVar } from '../../../apollo/inMemoryVar';
import { Page } from '../../layout';
import { appraisalViewCustomizationReports, STORAGE_KEYS } from '../appraisalViewCache';
import CustomizationTableSection from './CustomizationTableSection';
import PageInstructions from '../../shared/components/PageInstructions';
import { getRoleAdjustedUrl } from '../../shared/services/getIndustryReportsPageUrlService';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import { systemRole } from '../../../apollo/cache';
import { getAdjustedBreadcrumbPathItems } from '../../shared/services/breadcrumbService';
import DeviceClassSelection from './selections/DeviceClassSelection';
import OhtSelection from './selections/OhtSelection';
import PracticeAreaSelection from './selections/PracticeAreaSelection';
import PracticeGroupLevelSelection from './selections/PracticeGroupLevelSelection';
import ProgramYearSelection from './selections/ProgramYearSelection';
import GET_MERGED_PRACTICE_AREAS_WITH_PRACTICES from '../queries/getMergedPracticeAreasWithPracticesQuery';
import ApolloError from '@shared/components/ApolloError';

const CustomizationPage = () => {
  const reports = useReactiveVar(appraisalViewCustomizationReports);
  const role = useReactiveVar(systemRole);

  const [practiceArea, setPracticeArea] = useState(null);
  const [ohts, setOhts] = useState([]);
  const [deviceClasses, setDeviceClasses] = useState([]);
  const [practiceGroupLevel, setPracticeGroupLevel] = useState(null);
  const [appraisalProgramYear, setAppraisalProgramYear] = useState(null);

  const [includeHigherLevels, setIncludeHigherLevels] = useState(false);
  const [includeHigherProgramYears, setIncludeHigherProgramYears] = useState(false);
  const [includeExactMatchedDeviceClasses, setIncludeExactMatchedDeviceClasses] = useState(false);
  const [includeExactMatchedOhts, setIncludeExactMatchedOhts] = useState(false);
  const [programYearMax, setProgramYearMax] = useState(null);

  const [showError, setShowError] = useState(false);

  const pageTitle = 'Customization';

  const description =
    'Build each industry appraisal table individually. First, select a Practice Area to explore. ' +
    'Then, select a Practice Group Level. Note: if desired, use the check box to ' +
    'include the selected level as well as higher levels. Finally, select a Program Year. ' +
    'All OHTs and Device Classes are included by default and can be optionally filtered by selecting one ' +
    'or more from the dropdown. Note: if desired, use the check box to limit results to those ' +
    'that match the selection exactly, excluding results that contain any other combination of ' +
    'device classes or OHTs. When ready, select Add New Table. ' +
    'After adding all desired tables, select View Report(s).';

  const breadcrumbPath = [
    { name: 'Data Reports', url: '/data-reports' },
    {
      name: 'Industry Reports',
      url: '/data-reports/industry-reports'
    },
    {
      name: pageTitle,
      url: '/data-reports/industry-reports/customization'
    }
  ];

  const adjustedBreadcrumbPathItems = getAdjustedBreadcrumbPathItems(breadcrumbPath, role.Id);

  const navigate = useNavigate();

  const {
    data: practiceAreaListData,
    error: practiceAreaListError,
    loading: practiceAreaListLoading
  } = useQuery(GET_MERGED_PRACTICE_AREAS_WITH_PRACTICES);

  const practiceAreaList = practiceAreaListData?.practiceAreasMerged || [];

  const selectedPracticeAreaData = practiceAreaList.find(
    x => practiceArea && x.InternalId === practiceArea.value
  );

  useEffect(() => {
    setPracticeGroupLevel(null);
  }, [practiceArea]);

  const clearSelections = () => {
    setPracticeArea(null);
    setOhts([]);
    setDeviceClasses([]);
    setPracticeGroupLevel(null);
    setAppraisalProgramYear(programYearMax === 1 ? { label: '1', value: 1 } : null);
    setIncludeHigherLevels(false);
    setIncludeHigherProgramYears(false);
    setIncludeExactMatchedDeviceClasses(false);
    setIncludeExactMatchedOhts(false);
  };

  const clearAll = () => {
    clearSelections();
    appraisalViewCustomizationReports([]);
    clearInMemoryVar(STORAGE_KEYS.APPRAISAL_VIEW_REPORTS);
    setShowError(false);
  };

  const isFormFilledOut = () =>
    practiceArea &&
    practiceArea.value > 0 &&
    practiceGroupLevel &&
    practiceGroupLevel.value > 0 &&
    appraisalProgramYear &&
    appraisalProgramYear.value > 0;

  const addReport = () => {
    if (!isFormFilledOut()) {
      setShowError(true);
      return;
    }

    setShowError(false);

    const newReports = cloneDeep(reports);
    newReports.push({
      practiceArea,
      practiceGroupLevel,
      appraisalProgramYear,
      deviceClasses,
      ohts,
      includeHigherLevels,
      includeHigherProgramYears,
      includeExactMatchedDeviceClasses,
      includeExactMatchedOhts,
      uuid: uuid()
    });

    appraisalViewCustomizationReports(newReports);
    setInMemoryVar(STORAGE_KEYS.APPRAISAL_VIEW_REPORTS, newReports);
    clearSelections();
    return true;
  };

  const viewReports = () => {
    if (isFormFilledOut()) {
      addReport();
    }
    navigate(
      getRoleAdjustedUrl('/data-reports/industry-reports/customization/appraisal-view', role.Id)
    );
  };

  if (practiceAreaListError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle}>
      <Breadcrumb pathItems={adjustedBreadcrumbPathItems} />

      <div className='appraisal-view-customization'>
        <PageInstructions
          title='Appraisal View Customization Instructions'
          description={description}
        />
        <div className='appraisal-view-customization__sections'>
          <PracticeAreaSelection
            practiceAreaList={practiceAreaList}
            practiceArea={practiceArea}
            setPracticeArea={setPracticeArea}
            loading={practiceAreaListLoading}
          />
          <PracticeGroupLevelSelection
            practiceGroupLevel={practiceGroupLevel}
            setPracticeGroupLevel={setPracticeGroupLevel}
            selectedPracticeArea={selectedPracticeAreaData}
            includeHigherLevels={includeHigherLevels}
            setIncludeHigherLevels={setIncludeHigherLevels}
          />
          <ProgramYearSelection
            appraisalProgramYear={appraisalProgramYear}
            setAppraisalProgramYear={setAppraisalProgramYear}
            setProgramYearMax={setProgramYearMax}
            includeHigherProgramYears={includeHigherProgramYears}
            setIncludeHigherProgramYears={setIncludeHigherProgramYears}
          />
          <DeviceClassSelection
            deviceClasses={deviceClasses}
            setDeviceClasses={setDeviceClasses}
            includeExactMatchedDeviceClasses={includeExactMatchedDeviceClasses}
            setIncludeExactMatchedDeviceClasses={setIncludeExactMatchedDeviceClasses}
          />
          <OhtSelection
            ohts={ohts}
            setOhts={setOhts}
            includeExactMatchedOhts={includeExactMatchedOhts}
            setIncludeExactMatchedOhts={setIncludeExactMatchedOhts}
          />
        </div>
        {reports.length > 0 && <CustomizationTableSection reports={reports} />}
        <div className='appraisal-view-customization__actions-wrapper'>
          {showError && (
            <div className='appraisal-view-customization__error'>
              Must select all fields in order to continue
            </div>
          )}
          <div className='appraisal-view-customization__actions'>
            <Button className='clear-button' onClick={clearAll}>
              Clear All
            </Button>
            <Button className='secondary-button' onClick={addReport}>
              Add New Table
            </Button>
            <Button className='primary-button' disabled={!reports.length} onClick={viewReports}>
              View Report(s)
            </Button>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default CustomizationPage;
